import { useUser } from "../contexts/UserContext";
import { usePosts } from "../contexts/PostContext";
import { useNavigate } from "react-router-dom";
import { Post } from "../types/board";
import CommentList from "./CommentList";
import { useState, useEffect, useRef } from "react";
import PollVoteBox from "./PollVoteBox";
import { supabase } from "../supabase/supabaseClient";
import { ThumbsUp } from "lucide-react";
import ShareBar from "./ShareBar";
import TiptapEditor from "../components/TiptapEditor";
import { ABTALK_CATEGORIES } from "../constants/categories";

// ⭐ hideShare 추가됨
export interface BoardDetailProps {
  post: Post;
  onClose: () => void;
  onRecommend: (id: number) => void;
  boardRoute: string;
  hideShare?: boolean;     // ⭐ 추가
}

// SNS 자동 embed 처리
function autoEmbedSNS(html: string) {
  html = html.replace(/https:\/\/x\.com\//g, "https://twitter.com/");
  html = html.replace(
    /(https:\/\/twitter\.com\/[A-Za-z0-9_]+\/status\/\d+)/gi,
    (url) =>
      `<blockquote class="twitter-tweet" style="margin:auto;">
         <a href="${url}"></a>
       </blockquote>`
  );

  html = html.replace(
    /(https:\/\/www\.instagram\.com\/(p|reel)\/[A-Za-z0-9_-]+)/gi,
    (url) =>
      `<blockquote 
         class="instagram-media"
         data-instgrm-permalink="${url}"
         data-instgrm-version="14"
         style="background:#FFF; border:0; margin:1px auto; max-width:540px; width:100%; border-radius:3px; box-shadow:none; padding:0;"
       >
       </blockquote>`
  );

  return html;
}

export default function BoardDetail({
  post,
  onClose,
  onRecommend,
  boardRoute,
  hideShare = false,   // 기본값 false
}: BoardDetailProps) {

  const { user } = useUser();
  const { removePost, updatePost, likes, myReactions, handleReaction } = usePosts();
  const navigate = useNavigate();

  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState(post.title);
  const [content, setContent] = useState(post.content);
  const [category, setCategory] = useState(post.category || "");

  const contentRef = useRef<HTMLDivElement>(null);

  const typeLabel: Record<string, string> = {
    artist: "예술가",
    worker: "종사자",
    fan: "애호가",
  };

  const isAbtalk =
    ["artist", "worker", "fan"].includes(post.board_type) ||
    (boardRoute && boardRoute.includes("ab-talk"));

  const isAnonymousBoard = post.board_type === "익명게시판";
  const isOwner = user?.id === post.author_id;
  const isAdmin = user?.userType === "admin";

  // 조회수 증가
  useEffect(() => {
    async function updateViewCount() {
      if (!post.id) return;
      await supabase
        .from("posts")
        .update({ views: (post.views ?? 0) + 1 })
        .eq("id", post.id);
    }
    updateViewCount();
  }, [post.id, post.views]);

  // 트위터 / 인스타 embed
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const win = window as any;
    let resizeInterval: NodeJS.Timer | null = null;

    // Twitter embed
    if (el.innerHTML.includes("twitter.com")) {
      const processTwitter = () => {
        if (win.twttr?.widgets) win.twttr.widgets.load();
      };

      if (!win.twttr) {
        const script = document.createElement("script");
        script.src = "https://platform.twitter.com/widgets.js";
        script.async = true;
        script.onload = () => {
          setTimeout(processTwitter, 50);
          setTimeout(processTwitter, 500);
        };
        document.body.appendChild(script);
      } else {
        setTimeout(processTwitter, 50);
        setTimeout(processTwitter, 500);
      }
    }

    // Instagram embed
    if (el.innerHTML.includes("instagram.com")) {
      const processInstagram = () => {
        if (win.instgrm?.Embeds) win.instgrm.Embeds.process();
        if (!resizeInterval) {
          resizeInterval = setInterval(() => {
            const iframes = el.querySelectorAll<HTMLIFrameElement>("iframe[src*='instagram.com']");
            iframes.forEach((iframe) => {
              iframe.style.width = "100%";
              iframe.style.maxWidth = "540px";
              try {
                const body = iframe.contentWindow?.document?.body;
                if (body) iframe.style.height = `${body.scrollHeight}px`;
              } catch {}
            });
          }, 2000);
        }
      };

      if (!win.instgrm) {
        const script = document.createElement("script");
        script.src = "https://www.instagram.com/embed.js";
        script.async = true;
        script.onload = () => {
          setTimeout(processInstagram, 50);
          setTimeout(processInstagram, 500);
        };
        document.body.appendChild(script);
      } else {
        setTimeout(processInstagram, 50);
        setTimeout(processInstagram, 500);
      }
    }

    return () => {
      if (resizeInterval) clearInterval(resizeInterval);
    };
  }, [post.content]);

  // --- 수정모드 저장 ---
  async function handleUpdate() {
    if (!title.trim()) {
      alert("제목을 입력하세요!");
      return;
    }
    await updatePost({ ...post, title, content, category });
    setEditMode(false);
  }

  const isPollOpen =
    !post.poll?.deadline || new Date(post.poll.deadline) > new Date();

  const formattedDeadline = post.poll?.deadline
    ? new Date(post.poll.deadline).toLocaleString()
    : null;

  const myVote = myReactions?.[`post|${post.id}`];

  function countLikes(vote: number) {
    return post.id
      ? likes.filter((l) => l.target_type === "post" && l.target_id === post.id && l.vote === vote)
          .length
      : 0;
  }

  // --------------------------
  //   수정 모드 UI
  // --------------------------
  if (editMode) {
    let categoryList: { code: string; label: string }[] = [];

    if (isAbtalk) {
      categoryList = ABTALK_CATEGORIES;
    }

    const currentLabel =
      categoryList.find((c) => c.code === category)?.label || "없음";

    return (
      <div className="bg-white rounded shadow p-8 max-w-2xl mx-auto flex flex-col gap-6 relative">
        <button
          onClick={() => setEditMode(false)}
          className="absolute right-6 top-6 text-gray-400 hover:text-black"
        >
          수정취소
        </button>

        {categoryList.length > 0 && (
          <div>
            <label className="font-semibold mb-1 block">
              카테고리 (현재: {currentLabel})
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="border rounded px-2 py-1"
            >
              {categoryList.map((cat) => (
                <option key={cat.code} value={cat.code}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <input
          className="border w-full mb-3 p-2 text-lg font-bold"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="제목"
        />

        <TiptapEditor
          content={content}
          setContent={setContent}
          imageUploading={false}
          onPollChange={() => {}}
          className="min-h-[300px]"
        />

        <div className="flex justify-end gap-2">
          <button className="bg-green-500 text-white px-5 py-2 rounded" onClick={handleUpdate}>
            저장
          </button>
        </div>
      </div>
    );
  }

  // --------------------------
  //      보기 모드 UI
  // --------------------------
  return (
    <div className="bg-white rounded shadow p-8 max-w-2xl mx-auto flex flex-col gap-8 relative">
      {/* 닫기 버튼 */}
      <button
        onClick={onClose}
        className="absolute right-6 top-6 text-gray-400 hover:text-black"
      >
        닫기
      </button>

      {/* 제목 + 메타정보 */}
      <div>
        <span className="font-bold text-xl">{post.title}</span>

        <div className="mb-2 text-xs text-gray-500 flex gap-4 mt-1">
          <span>
            {isAbtalk
              ? `익명 ${typeLabel[post.writer_type || post.board_type] || "유저"}`
              : isAnonymousBoard
              ? "익명"
              : post.nickname || "익명"}
          </span>

          <span>
            {post.date ? new Date(post.date).toLocaleString() : ""}
          </span>

          <span>조회수 {post.views || 0}</span>
        </div>
      </div>

      {/* 투표 */}
      {post.poll?.question && typeof post.id === "number" && (
        isPollOpen ? (
          <PollVoteBox postId={post.id} poll={post.poll} />
        ) : (
          <div>
            <PollVoteBox postId={post.id} poll={post.poll} readonly />
            {formattedDeadline && (
              <p className="text-xs text-gray-400 mt-1">
                ※ 투표 종료 (마감: {formattedDeadline})
              </p>
            )}
          </div>
        )
      )}

      {/* 본문 */}
      <div
        className="mb-4 prose max-w-none"
        ref={contentRef}
        dangerouslySetInnerHTML={{ __html: autoEmbedSNS(post.content) }}
      />

      {/* ⭐ 공유링크 (익명게시판이면 숨김) */}
      {!hideShare && <ShareBar title={post.title} />}

      {/* 댓글 */}
      {post.id && <CommentList postId={post.id} boardType={post.board_type} />}

      {/* 추천 */}
      <div className="flex gap-4 justify-center mb-2">
        <button
          className={myVote === 1 ? "text-blue-600 font-bold" : "text-gray-400"}
          onClick={() => post.id && handleReaction("post", post.id, 1)}
          disabled={!user}
        >
          <ThumbsUp size={18} strokeWidth={2} />
          {countLikes(1)}
        </button>
      </div>

      {/* 수정 / 삭제 버튼 */}
      {(isOwner || isAdmin) && (
        <div className="flex justify-end gap-3 mt-10">
          <button
            className="text-green-600 border border-green-600 rounded px-4 py-2"
            onClick={() => setEditMode(true)}
          >
            수정
          </button>

          <button
            className="text-red-500 border border-red-500 rounded px-4 py-2"
            onClick={async () => {
              if (window.confirm("정말 삭제하시겠습니까?")) {
                await removePost(post.id!);
                if (onClose) onClose();
                else navigate(boardRoute);
              }
            }}
          >
            삭제
          </button>
        </div>
      )}
    </div>
  );
}
